import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import {
  TextField,
  Button,
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Avatar,
  CircularProgress,
  Tabs,
  Tab,
  Alert
} from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { useGoogleLogin } from '@react-oauth/google';
import { useAuth } from '../hooks/useAuth';
import api from '../utils/authUtils';

function AuthForm({ isUnconfirmed }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { login, register, resendConfirmationEmail, isAuthenticated, loading } = useAuth();
  
  const [activeTab, setActiveTab] = useState(0);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    first_name: '',
    last_name: ''
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showResendOption, setShowResendOption] = useState(false);
  
  useEffect(() => {
    if (isUnconfirmed) {
      setError('Please confirm your email before logging in.');
      setShowResendOption(true);
    }
  }, [isUnconfirmed]);
  

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get('tab');
    setActiveTab(tab === 'register' ? 1 : 0);
  }, [location.search]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevData => ({
      ...prevData,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    setSuccess('');
    setShowResendOption(false);

    try {
      if (activeTab === 0) { // Login
        await login({ 
          email: formData.email, 
          password: formData.password 
        });
        window.location.reload();
      } else { // Register
        await register(formData);
        setSuccess('Registration successful. Please check your email to confirm your account.');
        setActiveTab(0);
        setFormData(prevData => ({
          ...prevData,
          password: ''
        }));
      }
    } catch (err) {
      if (err.response && err.response.data) {
        const { error_code, message } = err.response.data;
        if (error_code === 'email_not_confirmed') {
          setError(message || 'Please confirm your email before logging in.');
          setShowResendOption(true);
        } else {
          setError(message || 'An error occurred. Please try again.');
        }
      } else {
        setError(err.message || 'An unexpected error occurred. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendConfirmation = async () => {
    try {
      await resendConfirmationEmail(formData.email);
      setSuccess('Confirmation email resent. Please check your inbox.');
      setShowResendOption(false);
    } catch (err) {
      setError('Failed to resend confirmation email. Please try again.');
    }
  };

  const handleGoogleSuccess = async (tokenResponse) => {
    try {
      setIsLoading(true);
      setError('');

      // Send the access token to our backend
      const response = await api.post('/oauth/google', {
        access_token: tokenResponse.access_token
      });

      // Save the JWT token from our backend
      localStorage.setItem('token', response.data.access_token);

      // Show success message
      if (response.data.is_new_user) {
        setSuccess('Welcome! Your account has been created.');
      } else {
        setSuccess('Login successful!');
      }

      // Redirect to dashboard after a short delay
      setTimeout(() => {
        window.location.href = '/dashboard';
      }, 1000);

    } catch (err) {
      console.error('Google OAuth error:', err);
      setError(err.response?.data?.message || 'Google login failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleError = (error) => {
    console.error('Google Login Error:', error);
    setError('Google login was cancelled or failed.');
  };

  const googleLogin = useGoogleLogin({
    onSuccess: handleGoogleSuccess,
    onError: handleGoogleError,
    flow: 'implicit'
  });

  const handleGoogleOAuth = () => {
    googleLogin();
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    setError('');
    setSuccess('');
    setShowResendOption(false);
    setFormData({
      username: '',
      email: '',
      password: '',
      first_name: '',
      last_name: ''
    });
    navigate(`/login?tab=${newValue === 0 ? 'login' : 'register'}`, { replace: true });
  };

  return (
    <Container maxWidth="xs">
      <Box sx={{
        mt: 8,
        mb: 4,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      }}>
        <Avatar sx={{ m: 1, bgcolor: 'secondary.main' }} aria-hidden="true">
          <LockOutlinedIcon />
        </Avatar>
        <Typography component="h1" variant="h5" id="auth-form-title">
          {activeTab === 0 ? 'Sign In' : 'Register'}
        </Typography>
        <Card sx={{ mt: 3, width: '100%', boxShadow: 3 }}>
          <CardContent>
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              centered
              aria-label="Authentication method selection"
            >
              <Tab label="Login" id="tab-login" aria-controls="tabpanel-login" />
              <Tab label="Register" id="tab-register" aria-controls="tabpanel-register" />
            </Tabs>
            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}
            {showResendOption && (
              <Box sx={{ mt: 2, textAlign: 'center' }}>
                <Typography variant="body2">
                  Didn't receive the confirmation email?{' '}
                  <Button 
                    color="primary" 
                    onClick={handleResendConfirmation}
                  >
                    Resend
                  </Button>
                </Typography>
              </Box>
            )}
            {success && <Alert severity="success" sx={{ mt: 2 }} role="status">{success}</Alert>}
            <Box
              component="form"
              onSubmit={handleSubmit}
              sx={{ mt: 1 }}
              role="tabpanel"
              id={`tabpanel-${activeTab === 0 ? 'login' : 'register'}`}
              aria-labelledby={`tab-${activeTab === 0 ? 'login' : 'register'}`}
            >
              {activeTab === 1 && (
                <>
                  <TextField
                    variant="outlined"
                    margin="normal"
                    required
                    fullWidth
                    label="Username"
                    name="username"
                    value={formData.username}
                    onChange={handleInputChange}
                    disabled={isLoading}
                    inputProps={{
                      'aria-label': 'Username',
                      'aria-required': 'true'
                    }}
                  />
                  <TextField
                    variant="outlined"
                    margin="normal"
                    fullWidth
                    label="First Name"
                    name="first_name"
                    value={formData.first_name}
                    onChange={handleInputChange}
                    disabled={isLoading}
                    inputProps={{
                      'aria-label': 'First Name'
                    }}
                  />
                  <TextField
                    variant="outlined"
                    margin="normal"
                    fullWidth
                    label="Last Name"
                    name="last_name"
                    value={formData.last_name}
                    onChange={handleInputChange}
                    disabled={isLoading}
                    inputProps={{
                      'aria-label': 'Last Name'
                    }}
                  />
                </>
              )}
              <TextField
                variant="outlined"
                margin="normal"
                required
                fullWidth
                label="Email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
                disabled={isLoading}
                inputProps={{
                  'aria-label': 'Email address',
                  'aria-required': 'true'
                }}
              />
              <TextField
                variant="outlined"
                margin="normal"
                required
                fullWidth
                label="Password"
                name="password"
                type="password"
                value={formData.password}
                onChange={handleInputChange}
                disabled={isLoading}
                inputProps={{
                  'aria-label': 'Password',
                  'aria-required': 'true'
                }}
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{
                  mt: 3,
                  mb: 2,
                  backgroundColor: '#1b58a0', /* Sfondo blu scuro */
                  color: '#ffffff', /* Testo bianco */
                  '&:hover': {
                    backgroundColor: '#184d8a', /* Blu leggermente più scuro per hover */
                  }
                }}
                disabled={isLoading}
                aria-label={activeTab === 0 ? 'Sign in to your account' : 'Register new account'}
              >
                {isLoading ? (
                  <>
                    <CircularProgress size={24} aria-label="Loading" />
                    <span className="sr-only">Processing...</span>
                  </>
                ) : (
                  activeTab === 0 ? 'Login' : 'Register'
                )}
              </Button>

              {activeTab === 0 && (
                <Box sx={{ textAlign: 'center', mt: 2, mb: 1 }}>
                  <Button
                    component={Link}
                    to="/forgot-password"
                    size="small"
                    sx={{ textTransform: 'none' }}
                  >
                    Forgot Password?
                  </Button>
                </Box>
              )}

              <Button
                fullWidth
                variant="contained"
                sx={{
                  mt: 1,
                  backgroundColor: '#ffffff', /* Sfondo bianco per contrastare */
                  color: '#1b58a0', /* Testo blu scuro */
                  '&:hover': {
                    backgroundColor: '#f0f0f0', /* Colore di hover leggermente più scuro */
                  }
                }}
                onClick={handleGoogleOAuth}
                aria-label={activeTab === 0 ? 'Sign in with Google' : 'Register with Google'}
              >
                {activeTab === 0 ? 'Login with Google' : 'Register with Google'}
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Container>
  );
}

export default AuthForm;
